import type { SitePass, UpcomingVisit } from '@/api/types/ProviderApi'

/**
 * The building's pass, from wherever this payload happens to carry it.
 *
 * A code or nothing. WeWork's email sometimes carries only a one-time link and no scannable QR,
 * and a link is not a thing a member can hold up at a desk — so where there is no code there is
 * no pass and no row, rather than a row that opens onto an apology.
 *
 * Two branches, and the second one is temporary. `sitePass` is the field: it comes down whenever
 * WeWork's email has landed, whatever the member has or has not done. Before that deploy the code
 * rode on the check-in block and was only revealed once the member was checked in — so a visit
 * from an older server can still show a pass, just not until arrival. The branch goes when the
 * deploy is everywhere.
 *
 * `siteName` is what names it in the fallback, because the old payload never said which building
 * the code was for.
 */
export const sitePassOf = (visit: UpcomingVisit): SitePass | null => {
	if (visit.sitePass?.code) {
		return visit.sitePass
	}

	if (visit.checkIn?.code) {
		return { name: visit.siteName ?? 'the building', code: visit.checkIn.code }
	}

	return null
}

/**
 * What the row says under the building's name: where to hold the thing up.
 *
 * The desk, not the gate. WeWork's own instruction is to check in at reception, and a member
 * reading "the gate" looks for a turnstile that half these buildings do not have.
 *
 * "Use", not "scan": a desk sometimes takes the code off the screen with a reader and sometimes
 * reads it out of a member's hand, and "scan" is an instruction to hold the phone up to a machine
 * that may not be there. No possessive either — "WeWork front desk" is the building's desk said
 * the way anybody says it, where "WeWork's front desk" is a sentence about who owns the furniture.
 */
export const passHint = (pass: SitePass) => `Use at ${pass.name} front desk`

/**
 * Whether the pass is still worth a row.
 *
 * The order at a WeWork is turnstile, then studio, then chair: the pass is how a member gets into
 * the building, and by the time they have told us they are here it has already done its job. So
 * the row goes when they check in — which is also the only way the card can say something changed
 * by getting shorter rather than by adding a panel.
 */
export const showsPass = (visit: UpcomingVisit) =>
	!!sitePassOf(visit) && visit.checkIn?.status !== 'CHECKED_IN'
